/**
 * This middleware responds to authentication actions. Cookies are
 * securely stored when a user logs in or signs up. Users are logged
 * out after a period of inactivity.
 *
 */
import { LOCATION_CHANGE } from 'stash/actions/router';
import { logIn } from 'stash/actions/session';
import { POST_USER_SUCCESS } from 'stash/actions/api/user';
import { STASH_INIT } from 'stash/actions/init';
import { WEB_APP_PATH } from 'stash/constants';
import { REFRESH_TOKEN_SUCCESS } from 'stash/actions/api/auth';
import { setCookie, deleteCookie, getCookie, formatAccessToken } from 'stash/utils';
import {
	LOG_IN,
	LOG_OUT,
	LOG_OUT_DEV,
	authSessionStart,
	logOut,
	logOutReasons,
} from 'stash/actions/session';
import {
	UUID_COOKIE_NAME,
	USER_ID_COOKIE_NAME,
	REFRESH_TOKEN_COOKIE_NAME,
	SESSION_TIMEOUT_INTERVAL,
	SESSION_TIMEOUT_INTERVAL_SECS,
	ACCESS_TOKEN_COOKIE_NAME,
	USER_CREATED_AT,
} from 'stash/constants/session';

let sessionTimeout;

const session =
	({ dispatch }) =>
	(next) =>
	(action) => {
		// At application start, dispatch AUTH_SESSION_START if logged in.
		if (action.type === STASH_INIT) {
			const id = getCookie(USER_ID_COOKIE_NAME);
			const uuid = getCookie(UUID_COOKIE_NAME);
			if (uuid) {
				next(action);

				return dispatch(authSessionStart(id || uuid, uuid));
			}
		}

		// Log in when a new user is created
		if (action.type === POST_USER_SUCCESS) {
			const apiKey = action.response.api_key;
			dispatch(logIn(apiKey.access_token, apiKey.user_id, action.response.user.uuid));
		}

		// Replace tokens upon refresh
		if (action.type === REFRESH_TOKEN_SUCCESS) {
			setCookie(
				ACCESS_TOKEN_COOKIE_NAME,
				formatAccessToken(action.response.access_token)
			);
			setCookie(REFRESH_TOKEN_COOKIE_NAME, action.response.refresh_token);
		}

		// Dispatch AUTH_SESSION_START on log in.
		if (action.type === LOG_IN) {
			dispatch(authSessionStart(action.userID, action.uuid));
		}

		// Store session cookies when user logs in.
		if (action.type === LOG_IN) {
			// Set cookies for web reg.
			setCookie(ACCESS_TOKEN_COOKIE_NAME, action.accessToken);
			setCookie(USER_ID_COOKIE_NAME, action.userID);
			setCookie(UUID_COOKIE_NAME, action.uuid);
			setCookie(USER_CREATED_AT, action.created_at);
		}

		// Delete session cookies and auth + redirect when user logs out.
		if (action.type === LOG_OUT) {
			deleteCookie(ACCESS_TOKEN_COOKIE_NAME);
			deleteCookie(REFRESH_TOKEN_COOKIE_NAME);
			deleteCookie(USER_ID_COOKIE_NAME);
			deleteCookie(UUID_COOKIE_NAME);
			deleteCookie(USER_CREATED_AT);

			// Do a hard redirect on window to clear memory.
			window.setTimeout(() => {
				window.location.href = WEB_APP_PATH + `/log-in?reason=${action.reason || ''}`;
			}, 300);
		}

		if (action.type === LOG_OUT_DEV) {
			deleteCookie(ACCESS_TOKEN_COOKIE_NAME);
			deleteCookie(USER_ID_COOKIE_NAME);
			deleteCookie(UUID_COOKIE_NAME);
			deleteCookie(USER_CREATED_AT);

			// Do a hard redirect on window to clear memory.
			window.setTimeout(() => {
				window.location.href = '/sign-up';
			}, 300);
		}

		// A location change is our definition of user 'activity'.
		// Reset the session timeouts when this happens.
		if (action.type === LOCATION_CHANGE) {
			if (
				getCookie(USER_ID_COOKIE_NAME) &&
				getCookie(ACCESS_TOKEN_COOKIE_NAME) &&
				getCookie(UUID_COOKIE_NAME)
			) {
				// Refresh session cookies.
				setCookie(USER_ID_COOKIE_NAME, getCookie(USER_ID_COOKIE_NAME), {
					'max-age': SESSION_TIMEOUT_INTERVAL_SECS,
				});
				setCookie(ACCESS_TOKEN_COOKIE_NAME, getCookie(ACCESS_TOKEN_COOKIE_NAME), {
					'max-age': SESSION_TIMEOUT_INTERVAL_SECS,
				});
				setCookie(UUID_COOKIE_NAME, getCookie(UUID_COOKIE_NAME), {
					'max-age': SESSION_TIMEOUT_INTERVAL_SECS,
				});
				setCookie(USER_CREATED_AT, getCookie(USER_CREATED_AT), {
					'max-age': SESSION_TIMEOUT_INTERVAL_SECS,
				});

				// Reset session timeout.
				window.clearTimeout(sessionTimeout);
				sessionTimeout = window.setTimeout(() => {
					dispatch(logOut(logOutReasons.TIMED_OUT));
				}, SESSION_TIMEOUT_INTERVAL);
			}
		}

		return next(action);
	};

export default session;
